import React, { useState } from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TextInput,
  View,
} from 'react-native';
import PageHeader from '../../components/account/PageHeader';
import Icon from '../../components/ui/Icon';
import Text from '../../components/ui/Text';
import {
  categoryLabel,
  statusLabel,
  Ticket,
  TicketMessage,
} from '../../data/support';
import {
  colors,
  hairline,
  inputText,
  radii,
  screenPadding,
  spacing,
  useResponsive,
} from '../../theme';

/**
 * One request, and how it ended.
 *
 * ---------------------------------------------------------------------------
 * THE OUTCOME IS THE POINT OF THIS SCREEN
 * ---------------------------------------------------------------------------
 * A thread of messages is easy. What matters is the panel at the bottom of a
 * closed ticket: the decision, the reasoning behind it, and the evidence it
 * was based on. A dispute closed with "Rejected" and nothing else does not end
 * the dispute - it converts it into an escalation, and then a review, and then
 * a chargeback.
 *
 * So the outcome panel refuses to draw without `resolutionNotes`, and a
 * rejection is given more room than a resolution rather than less: the
 * customer who was told no is the one who needs to understand why.
 *
 * A rejected ticket keeps its reply box, with the one thing that would change
 * the answer spelled out. "Not upheld" should never be a dead end.
 */

type Props = {
  ticket: Ticket;
  onBack: () => void;
};

function TicketThreadScreen({ ticket, onBack }: Props) {
  const resp = useResponsive();
  const [text, setText] = useState('');
  /** Replies typed this session. Never persisted - there is nowhere to send. */
  const [drafts, setDrafts] = useState<TicketMessage[]>([]);

  const closed = ticket.status === 'resolved' || ticket.status === 'rejected';
  const rejected = ticket.status === 'rejected';
  const messages = [...ticket.messages, ...drafts];

  const send = () => {
    const body = text.trim();
    if (!body) {
      return;
    }
    setDrafts(current => [
      ...current,
      { id: `d${current.length + 1}`, from: 'you', body, when: 'Just now' },
    ]);
    setText('');
  };

  return (
    <KeyboardAvoidingView
      style={styles.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
    >
      <PageHeader title={categoryLabel(ticket.category)} onBack={onBack} />

      <ScrollView
        style={styles.scroller}
        contentContainerStyle={[styles.page, resp.page]}
        showsVerticalScrollIndicator={false}
      >
        {/* ---------------------------------------------------- the header */}
        <View style={styles.card}>
          <Text variant="subtitle">{ticket.subject}</Text>
          <View style={styles.facts}>
            <Text variant="caption" color={colors.inkMuted}>
              {ticket.reference}
            </Text>
            {ticket.bookingRef ? (
              <Text variant="caption" color={colors.inkMuted}>
                · {ticket.bookingRef}
              </Text>
            ) : null}
            <Text variant="caption" color={colors.inkMuted}>
              · Raised {ticket.createdOn}
            </Text>
          </View>
          <View style={styles.chip}>
            <Text variant="caption" color={colors.inkBody}>
              {statusLabel[ticket.status]}
            </Text>
          </View>
        </View>

        {/* -------------------------------------------------- the messages */}
        {messages.map(message => {
          const mine = message.from === 'you';
          return (
            <View
              key={message.id}
              style={[styles.row, mine ? styles.rowMine : null]}
            >
              <View
                style={[
                  styles.bubble,
                  { maxWidth: Math.min(resp.contentWidth * 0.8, 420) },
                  mine ? styles.bubbleMine : styles.bubbleTheirs,
                ]}
              >
                <Text
                  variant="bodySm"
                  color={mine ? colors.inkInverse : colors.inkBody}
                >
                  {message.body}
                </Text>
                <Text
                  variant="caption"
                  color={mine ? colors.inkInverseMuted : colors.inkMuted}
                  style={styles.stamp}
                >
                  {message.when}
                </Text>
              </View>
            </View>
          );
        })}

        {/* ---------------------------------------------------- the outcome */}
        {closed && ticket.resolutionNotes ? (
          <View style={[styles.outcome, rejected ? styles.outcomeNo : null]}>
            <View style={styles.outcomeHead}>
              <Icon
                name={rejected ? 'alert' : 'checkCircle'}
                size={18}
                color={rejected ? colors.inkBody : colors.primary}
              />
              <Text variant="labelSm" color={colors.inkBody}>
                {rejected ? 'Not upheld' : 'Resolved'} · {ticket.updatedOn}
              </Text>
            </View>

            <Text variant="bodySm" color={colors.inkBody}>
              {ticket.resolutionNotes}
            </Text>

            {/* What it was decided on. Named rather than shown - the customer
                cannot open a route trail or a professional's proof photo, and
                a list of what was weighed is what makes this a decision rather
                than a shrug. */}
            {ticket.evidence?.length ? (
              <View style={styles.evidence}>
                <Text variant="caption" color={colors.inkMuted}>
                  Based on
                </Text>
                {ticket.evidence.map(item => (
                  <View key={item} style={styles.evidenceRow}>
                    <Icon name="check" size={13} color={colors.inkMuted} strokeWidth={2.2} />
                    <Text
                      variant="caption"
                      color={colors.inkBody}
                      style={styles.grow}
                    >
                      {item}
                    </Text>
                  </View>
                ))}
              </View>
            ) : null}

            {/* Never a dead end. */}
            {rejected ? (
              <Text variant="caption" color={colors.inkMuted}>
                If you have something we have not seen - photos of the work, or
                a message from your professional - reply below and we will
                reopen this.
              </Text>
            ) : null}
          </View>
        ) : null}
      </ScrollView>

      {/* ------------------------------------------------------- the reply */}
      <View
        style={[
          styles.composer,
          { paddingHorizontal: screenPadding + resp.sideInset },
        ]}
      >
        <TextInput
          value={text}
          onChangeText={setText}
          placeholder={rejected ? 'Add what we have not seen' : 'Reply to support'}
          placeholderTextColor={colors.inkSubtle}
          multiline
          style={styles.input}
          accessibilityLabel="Your reply"
        />
        <Pressable
          accessibilityRole="button"
          accessibilityLabel="Send reply"
          accessibilityState={{ disabled: !text.trim() }}
          disabled={!text.trim()}
          onPress={send}
          style={({ pressed }) => [
            styles.send,
            !text.trim() ? styles.sendOff : null,
            pressed ? styles.pressed : null,
          ]}
        >
          <Icon
            name="arrowRight"
            size={18}
            color={text.trim() ? colors.onPrimary : colors.inkSubtle}
            strokeWidth={2}
          />
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: colors.background,
  },
  scroller: {
    flex: 1,
  },
  page: {
    /* Vertical only: the horizontal gutter comes from `resp.page` on the
       contentContainer, which also carries the tablet inset. `padding`
       here was silently overridden by it. */
    paddingVertical: screenPadding,
    gap: spacing.md,
    paddingBottom: spacing.xl,
  },
  card: {
    gap: spacing.xs,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surface,
    ...hairline,
  },
  facts: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  chip: {
    alignSelf: 'flex-start',
    marginTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
  },
  row: {
    alignItems: 'flex-start',
  },
  rowMine: {
    alignItems: 'flex-end',
  },
  bubble: {
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: radii.md,
  },
  bubbleTheirs: {
    backgroundColor: colors.surface,
    ...hairline,
  },
  bubbleMine: {
    backgroundColor: colors.surfaceInverse,
  },
  stamp: {
    alignSelf: 'flex-end',
    marginTop: 2,
  },
  /*
   * The decision, in the pale accent rather than a success green or a danger
   * red. Both endings are legitimate outcomes of the same process, and
   * colouring a rejection like an error tells the customer the app broke
   * rather than that a person decided.
   */
  outcome: {
    gap: spacing.sm,
    padding: spacing.lg,
    borderRadius: radii.lg,
    backgroundColor: colors.surfaceAccent,
  },
  outcomeNo: {
    backgroundColor: colors.surfaceMuted,
  },
  outcomeHead: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  evidence: {
    gap: spacing.xs,
    paddingTop: spacing.sm,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  evidenceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
  },
  composer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: spacing.sm,
    paddingVertical: spacing.md,
    backgroundColor: colors.surface,
    borderTopWidth: hairline.borderWidth,
    borderTopColor: hairline.borderColor,
  },
  input: {
    flex: 1,
    ...inputText,
    maxHeight: 96,
    minHeight: 40,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: radii.md,
    backgroundColor: colors.surfaceMuted,
  },
  send: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.primary,
  },
  sendOff: {
    backgroundColor: colors.surfaceMuted,
  },
  grow: {
    flex: 1,
  },
  pressed: {
    opacity: 0.7,
  },
});

export default TicketThreadScreen;
